import { createApi } from '@reduxjs/toolkit/query/react';
import { IWorkItemThresholds, IWrapUpTimer } from '../../models/agent';
import { createRbacBaseQuery, del, post, put } from '../../utils/helpers';

const blenderApi = createApi({
  reducerPath: 'blender',
  tagTypes: ['WrapUpTimer', 'DefaultWorkItemThresholds', 'AgentWorkItemThresholds', 'BreakNames', 'WorkItemCompletionCodes'],
  baseQuery: createRbacBaseQuery(''),
  endpoints: (builder) => ({
    getWrapUpTimer: builder.query<IWrapUpTimer, void>({
      query: () => '/Wrapup?api-version=3.0',
      providesTags: ['WrapUpTimer'],
    }),
    updateWrapUpTimer: builder.mutation<IWrapUpTimer, IWrapUpTimer>({
      query: (wrapUpTimer) => post('/Wrapup?api-version=3.0', wrapUpTimer),
      invalidatesTags: ['WrapUpTimer'],
    }),
    getDefaultThresholds: builder.query<IWorkItemThresholds, void>({
      query: () => '/workItemThresholds?api-version=3',
      providesTags: ['DefaultWorkItemThresholds'],
    }),
    updateDefaultThresholds: builder.mutation<void, IWorkItemThresholds>({
      query: (thresholds) => put('/workItemThresholds?api-version=3', thresholds),
      invalidatesTags: ['DefaultWorkItemThresholds'],
    }),
    getAgentThresholds: builder.query<IWorkItemThresholds, string>({
      query: (agentId) => `/agents/${agentId}/workItemThresholds?api-version=3`,
      providesTags: (_, __, agentId) => [{ type: 'AgentWorkItemThresholds', id: agentId }],
    }),
    setAgentThresholds: builder.mutation<void, { agentId: string, thresholds: IWorkItemThresholds }>({
      query: ({ agentId, thresholds: tresholds }) => put(`/agents/${agentId}/workItemThresholds?api-version=3`, tresholds),
      invalidatesTags: (_, __, { agentId }) => [{ type: 'AgentWorkItemThresholds', id: agentId }],
    }),
    unsetAgentThresholds: builder.mutation<void, string>({
      query: (agentId) => del(`/agents/${agentId}/workItemThresholds?api-version=3`),
      invalidatesTags: (_, __, agentId) => [{ type: 'AgentWorkItemThresholds', id: agentId }],
    }),
    getBreakNames: builder.query<string[], void>({
      query: () => '/breaknames?api-version=3.0',
      providesTags: ['BreakNames'],
    }),
    updateBreakNames: builder.mutation<void, string[]>({
      query: (breakNames) => put('/breaknames?api-version=3.0', breakNames),
      invalidatesTags: ['BreakNames'],
    }),
    getWorkItemCompletionCodes: builder.query<string[], string>({
      query: (channelId) => `/channels/${channelId}/WorkItemCompletionCodes`,
      providesTags: (_, __, channelId) => [{ type: 'WorkItemCompletionCodes', id: channelId }],
    }),
    setWorkItemCompletionCodes: builder.mutation<void, { channelId: string, workItemCompletionCodes: string[] }>({
      query: ({ channelId, workItemCompletionCodes }) => post(`/channels/${channelId}/WorkItemCompletionCodes`, workItemCompletionCodes),
      invalidatesTags: (_, __, { channelId }) => [{ type: 'WorkItemCompletionCodes', id: channelId }],
    }),
  }),
});

export default blenderApi;

export const {
  useGetWrapUpTimerQuery,
  useUpdateWrapUpTimerMutation,
  useGetDefaultThresholdsQuery,
  useUpdateDefaultThresholdsMutation,
  useGetAgentThresholdsQuery,
  useSetAgentThresholdsMutation,
  useUnsetAgentThresholdsMutation,
  useGetBreakNamesQuery,
  useUpdateBreakNamesMutation,
  useGetWorkItemCompletionCodesQuery,
  useSetWorkItemCompletionCodesMutation,
} = blenderApi;